/**
 * NextStepPrompt - "What's the next step?" (T-04).
 *
 * Opens when someone completes the LAST open task on an active/cold lead or an
 * open opportunity. The only ways out are the ones Paul listed: save the next
 * task, set the lead Inactive or Dead, or close the opportunity (Won / Lost /
 * Cancelled / No Quote). Cancel does nothing at all - the task stays open.
 * Closing as Won goes on to the PO form (useCompleteTask, Q-02).
 *
 * Driven by useCompleteTask, which completes the task only after one of those
 * succeeds. It opens after the "Complete task" box (T-09), so any note typed
 * there is shown here and saved with the completion. Rendered into document.body: the Dashboard's side column is a
 * size container, which would otherwise trap a fixed overlay inside it.
 */
import { useMemo, useState } from 'react';
import { useDraggableDialog } from '../../hooks/useDraggableDialog';
import { createPortal } from 'react-dom';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { LEAD_STATUS_DEFINITIONS } from '../../data/crmStages';
import { contactsForHome, indexById, LEAD_EXIT_STATUSES, OPP_CLOSE_CHOICES } from '../crm/nextStep';
import { ContactSelect } from './TaskComposer';
import TagPeople from './TagPeople';
import { tidyTagged } from './tagged';

const GREEN = '#1B5E20';
const FONT = "'Montserrat', sans-serif";
const input = {
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #D8DED4',
  fontSize: 12,
  fontFamily: FONT,
  outline: 'none',
  background: '#fff',
  color: '#0D0D0D',
  width: '100%',
  boxSizing: 'border-box',
};
const label = {
  fontSize: 10,
  fontWeight: 700,
  color: '#5A5A5A',
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  marginBottom: 4,
  display: 'block',
};
const exitBtn = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #D0D0D0',
  background: '#fff',
  color: '#444',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: FONT,
};
const OPP_CLOSE_HINT = {
  Won: 'PO received',
  Lost: 'Lost to someone else',
  Cancelled: 'Not awarded or cancelled',
  'No Quote': 'We backed out',
};

export default function NextStepPrompt({ task, home, note, onSave, onSetLeadStatus, onCloseOpportunity, onCancel }) {
  const drag = useDraggableDialog(); // moved by its header (10/9/26)
  const { users, allAccounts, allProjects } = useApp();
  const { user } = useAuth();
  const me = (user && user.name) || task.owner || '';
  const [description, setDescription] = useState('');
  const [owner, setOwnerRaw] = useState(me);
  const [tagged, setTagged] = useState([]);
  // the new owner comes off the tagged list
  const setOwner = (name) => {
    setOwnerRaw(name);
    setTagged((cur) => tidyTagged(cur, name));
  };
  const [due, setDue] = useState('');
  const [contactId, setContactId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const people = useMemo(
    () =>
      Array.from(new Set([...(users || []).map((u) => u.name).filter(Boolean), ...(owner ? [owner] : [])])).sort((a, b) =>
        a.localeCompare(b)
      ),
    [users, owner]
  );
  const contacts = useMemo(
    () => contactsForHome(home.kind === 'lead' ? 'account' : 'project', home.id, indexById(allAccounts), indexById(allProjects)),
    [home, allAccounts, allProjects]
  );

  const isLead = home.kind === 'lead';
  const canSave = description.trim() && owner && due && !busy;

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      const err = await fn();
      if (err) setError(err);
    } finally {
      setBusy(false);
    }
  };
  const save = () => {
    if (!canSave) return;
    run(() =>
      onSave({ description: description.trim(), owner, due_date: due, contact_id: contactId || null, tagged: tidyTagged(tagged, owner) })
    );
  };
  const exitLead = (status) => {
    if (
      busy ||
      !window.confirm(
        `Set ${home.label} to ${status}?\n\n${LEAD_STATUS_DEFINITIONS[status] || ''}\nIt leaves the Leads board and needs no next step.`
      )
    )
      return;
    run(() => onSetLeadStatus(status));
  };
  const exitOpp = (status) => {
    if (busy) return;
    // not won = say why (11/3/26); the completion note is the starting text
    if (status !== 'Won') {
      const why = window.prompt(`Mark ${home.label} as ${status}? Say why (required):`, note || '');
      if (why && why.trim()) run(() => onCloseOpportunity(status, why.trim()));
      return;
    }
    if (!window.confirm(`Mark ${home.label} as ${status}?`)) return;
    run(() => onCloseOpportunity(status));
  };

  const stop = (e) => e.stopPropagation();
  const body = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="next-step-title"
      onClick={stop}
      onMouseDown={stop}
      onContextMenu={stop}
      onKeyDown={(e) => {
        // stop here so the editor underneath does not also close on Escape
        e.stopPropagation();
        if (e.key === 'Escape' && !busy) onCancel();
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        fontFamily: FONT,
      }}
    >
      <div
        className="go-pop-in"
        ref={drag.ref}
        onPointerDown={drag.onPointerDown}
        style={{
          width: '100%',
          maxWidth: 500,
          maxHeight: '92vh',
          overflowY: 'auto',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 18px 50px rgba(0,0,0,0.25)',
          boxSizing: 'border-box',
          ...drag.style,
        }}
      >
        <div data-dialog-handle="" style={{ padding: '18px 22px 12px', borderBottom: '1px solid #EEF1EA' }}>
          <div style={{ fontSize: 9.5, fontWeight: 800, color: '#B07503', letterSpacing: 1.4, textTransform: 'uppercase' }}>
            Last open task on this {isLead ? 'lead' : 'opportunity'}
          </div>
          <div id="next-step-title" style={{ fontSize: 17, fontWeight: 800, color: '#0D0D0D', marginTop: 4 }}>
            What&apos;s the next step?
          </div>
          <div style={{ fontSize: 11.5, color: '#555', marginTop: 6, lineHeight: 1.5 }}>
            Completing <b>&ldquo;{task.description}&rdquo;</b> leaves <b>{home.label}</b> with no open task. Every{' '}
            {isLead ? 'active or cold lead' : 'open opportunity'} keeps at least one.
          </div>
          {note ? (
            <div
              style={{
                fontSize: 11,
                color: '#555',
                marginTop: 8,
                background: '#F6F9F3',
                border: '1px solid #E5E9DD',
                borderRadius: 6,
                padding: '6px 9px',
                lineHeight: 1.45,
                wordBreak: 'break-word',
              }}
            >
              Your note goes on the timeline with the completed task: <i>{note}</i>
            </div>
          ) : null}
        </div>

        <div style={{ padding: '14px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label style={label} htmlFor="next-step-task">
              Next task *
            </label>
            <input
              id="next-step-task"
              autoFocus
              style={input}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What needs to happen next?"
            />
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 180px' }}>
              <label style={label} htmlFor="next-step-owner">
                Owner *
              </label>
              <select id="next-step-owner" style={{ ...input, cursor: 'pointer' }} value={owner} onChange={(e) => setOwner(e.target.value)}>
                <option value="">- Assign to -</option>
                {people.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: '0 1 150px' }}>
              <label style={label} htmlFor="next-step-due">
                Due date *
              </label>
              <input id="next-step-due" type="date" style={input} value={due} onChange={(e) => setDue(e.target.value)} />
            </div>
          </div>
          <div>
            <label style={label} htmlFor="next-step-tagged">
              Tag people (optional)
            </label>
            <TagPeople id="next-step-tagged" value={tagged} onChange={setTagged} people={people} owner={owner} />
          </div>
          {contacts.length > 0 && (
            <div>
              <span style={label}>Contact (optional)</span>
              <ContactSelect contacts={contacts} value={contactId} onChange={setContactId} style={{ ...input, cursor: 'pointer' }} />
            </div>
          )}

          {error && (
            <div
              style={{
                background: '#FFEBEE',
                border: '1px solid #FFCDD2',
                color: '#B71C1C',
                borderRadius: 6,
                padding: '7px 10px',
                fontSize: 11.5,
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2 }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              title="Nothing changes - the task stays open"
              style={{
                padding: '8px 14px',
                borderRadius: 6,
                border: '1px solid #DDD',
                background: '#fff',
                color: '#555',
                fontSize: 11.5,
                fontWeight: 600,
                cursor: busy ? 'default' : 'pointer',
                fontFamily: FONT,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!canSave}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                fontSize: 11.5,
                fontWeight: 700,
                fontFamily: FONT,
                background: canSave ? GREEN : '#C8D6C9',
                color: '#fff',
                cursor: canSave ? 'pointer' : 'not-allowed',
              }}
            >
              {busy ? 'Saving…' : 'Save next task and complete'}
            </button>
          </div>
        </div>

        <div style={{ padding: '12px 22px 18px', background: '#FAFBF7', borderTop: '1px solid #EEF1EA' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#444', marginBottom: 8 }}>
            No next step? {isLead ? 'Set this lead to:' : 'Close this opportunity as:'}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {isLead
              ? LEAD_EXIT_STATUSES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={busy}
                    onClick={() => exitLead(s)}
                    title={LEAD_STATUS_DEFINITIONS[s]}
                    style={exitBtn}
                  >
                    {s}
                  </button>
                ))
              : OPP_CLOSE_CHOICES.map((s) => (
                  <button key={s} type="button" disabled={busy} onClick={() => exitOpp(s)} title={OPP_CLOSE_HINT[s]} style={exitBtn}>
                    {s}
                  </button>
                ))}
          </div>
          <div style={{ fontSize: 10, color: '#888', marginTop: 8 }}>Either one completes the task. Cancel leaves it open.</div>
        </div>
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(body, document.body) : body;
}
